"use client";

import React, { FC, useState } from "react";
import { useI18n } from "@/i18n/client";
import clsx from "clsx";
import { Cart } from "@/lib/5874/types";
import { CartDetailsType, CartShippingSettingsType } from "../CartTypes";
import IconOnlyButton from "@/components/UI/Button/IconOnlyButton/IconOnlyButton";
import Collapsible from "@/components/UI/Collapsible/Collapsible";
import Markdown from "@/components/UI/Markdown/Markdown";
import FormattedPrice from "@/components/UI/FormattedPrice/FormattedPrice";
import { CheckoutButton } from "../CheckoutButton/CheckoutButton";
import UpArrowIcon from "@/app/assets/images/svg/movora-arrow.svg";
import { removeCouponCodeFromCheckout } from "@/lib/bigcommerce/api/discount/discount";
import { StoreRegion } from "@/lib/_config/stores";
import { useCartContext } from "@/providers/CartContextProvider";
import styles from "./CartSummary.module.scss";
import { setGlobalDiscountMessageType } from "@/hooks/useDiscount";

interface CartSummaryProps {
	cart?: Cart; // Make cart prop optional since we'll use context
	creditInfo:
		| {
				enabled: boolean;
				amount: number;
		  }
		| undefined;
	cartSummary: CartDetailsType;
	shippingSettings: CartShippingSettingsType;
	store: StoreRegion;
}

export const CartSummary: FC<CartSummaryProps> = ({
	cart: cartProp, // Rename to avoid conflict with context cart
	creditInfo,
	cartSummary,
	shippingSettings,
	store,
}) => {
	const t = useI18n();
	const { cart: contextCart, refreshCart } = useCartContext();

	// Use context cart if available, otherwise fall back to prop
	const cart = contextCart || cartProp;

	const [isCutOffSectionOpened, setIsCutOffSectionOpened] = useState(
		shippingSettings.isOpenedByDefault,
	);
	const [isRemovingCoupon, setIsRemovingCoupon] = useState<string | null>(null);

	const handleRemoveCoupon = async (couponCode: string) => {
		if (!cart?.id || isRemovingCoupon) {
			return;
		}

		setIsRemovingCoupon(couponCode);

		try {
			const result = await removeCouponCodeFromCheckout(
				cart.id,
				couponCode,
				store,
			);
			if (result?.status === 200) {
				await refreshCart();
				setGlobalDiscountMessageType("REMOVED");
			} else {
				console.error("Failed to remove coupon");
				setGlobalDiscountMessageType("FAILURE");
			}
		} catch (error) {
			console.error("Error removing coupon:", error);
		} finally {
			setIsRemovingCoupon(null);
		}
	};

	// Safety check - if no cart is available, don't render
	if (!cart) {
		return null;
	}

	return (
		<div className={styles.cartSummary}>
			{cartSummary?.title && (
				<h2 className={styles.title}>{cartSummary?.title}</h2>
			)}

			<div className={styles.subTotal}>
				<div>{t("Cart.subtotal")}</div>
				<FormattedPrice price={cart.base_amount} />
			</div>
			{shippingSettings.title && shippingSettings.content && (
				<>
					<div className={styles.cutOff}>
						<div>{shippingSettings.title}</div>
						<IconOnlyButton
							className={clsx([
								styles.arrow,
								!isCutOffSectionOpened && styles.cutOffIconActive,
							])}
							variant='empty'
							onClick={() => setIsCutOffSectionOpened(!isCutOffSectionOpened)}
							icon={<UpArrowIcon />}
						/>
					</div>
					<Collapsible isOpen={isCutOffSectionOpened}>
						<Markdown
							className={styles.cutOffDescription}
							content={shippingSettings.content}
						/>
					</Collapsible>
				</>
			)}
			{creditInfo?.enabled && (
				<div className={styles.availableCredit}>
					<div>{t("Cart.availableCredit")}</div>
					<div>
						-<FormattedPrice price={creditInfo.amount} />
					</div>
				</div>
			)}

			{cart.coupons.length > 0 &&
				cart.coupons.map((coupon) => (
					<div className={styles.subTotal}>
						<div>
							{coupon.name}
							<button
								onClick={() => handleRemoveCoupon(coupon.code)}
								disabled={isRemovingCoupon === coupon.code}
								className={styles.removeCouponButton}
							>
								{isRemovingCoupon === coupon.code ? "removing..." : "remove"}
							</button>
						</div>

						<span>
							- <FormattedPrice price={coupon.discounted_amount} />
						</span>
					</div>
				))}

			<div className={styles.orderTotal}>
				<div className={styles.title}>{t("Cart.orderTotal")}</div>
				<div>
					<FormattedPrice price={cart.cart_amount} />
				</div>
			</div>

			<Markdown
				className={styles.orderTotalDescription}
				content={cartSummary.content}
			/>
			<CheckoutButton className={styles.checkoutButton}>
				{t("Cart.toCheckout")}
			</CheckoutButton>
		</div>
	);
};
